/**
 * Block: _shpflt-header-announcement
 * Live countdown custom element rendered inside each announcement block that
 * has `date__enable` turned on.
 *
 * The element is connected to the DOM AFTER shpflt-header.js teleports it
 * from the hidden blocks-source div into the promo bar, so `connectedCallback`
 * fires at the correct time with no extra coordination required.
 */

import { ShopigurusHTMLElement } from '@shopigurus/base';

/**
 * ShpfltAnnouncementCountdown — Ticking countdown timer for promo bar items.
 * Reads the target date from `data-date`, ticks every second, and clears
 * itself when the date passes.
 *
 * @example
 * <shpflt-announcement-countdown data-date="31 Dec 2026 23:59:59">
 * </shpflt-announcement-countdown>
 */
class ShpfltAnnouncementCountdown extends ShopigurusHTMLElement {
  /** @type {number|null} */
  #interval = null;

  connectedCallback() {
    super.connectedCallback();

    const target = new Date(this.dataset.date).getTime();
    if (isNaN(target)) return;

    // Expose the target date as an accessible label so screen reader users can
    // discover the purpose of this element without experiencing per-second updates.
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', `Ends ${this.dataset.date}`);
    }

    this.#tick(target);
    this.#interval = setInterval(() => this.#tick(target), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#interval);
    this.#interval = null;
  }

  /**
   * Re-renders the countdown display for the given target timestamp.
   * Clears the interval automatically when the countdown expires.
   * @param {number} target - Target timestamp in milliseconds
   */
  #tick(target) {
    const distance = target - Date.now();

    if (distance < 0) {
      clearInterval(this.#interval);
      this.#interval = null;
      this.innerHTML = '';
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    const pad = (/** @type {number} */ n) =>
      String(n).padStart(2, '0');

    this.innerHTML =
      `<span class="shpflt-header-announcement__countdown-unit">` +
        `<span class="shpflt-header-announcement__unit-time">${pad(days)}</span>` +
        `<span>DAYS</span>` +
      `</span>` +
      `<span class="shpflt-header-announcement__countdown-unit">` +
        `<span class="shpflt-header-announcement__unit-time">${pad(hours)}</span>` +
        `<span>HOURS</span>` +
      `</span>` +
      `<span class="shpflt-header-announcement__countdown-unit">` +
        `<span class="shpflt-header-announcement__unit-time">${pad(minutes)}</span>` +
        `<span>MIN</span>` +
      `</span>` +
      `<span class="shpflt-header-announcement__countdown-unit">` +
        `<span class="shpflt-header-announcement__unit-time">${pad(seconds)}</span>` +
        `<span>SEC</span>` +
      `</span>`;
  }
}

customElements.define('shpflt-announcement-countdown', ShpfltAnnouncementCountdown);
